/**
 * F08: Booking Window Resolution Utility
 *
 * Maps selected date to API window parameter ('today', 'tomorrow', 'previous').
 * The date parameter itself is passed separately for exact date routing.
 */

/**
 * Parse various date value types to Date object
 */
const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const pad = (value) => String(value).padStart(2, '0');

export const formatBookingsDate = (value = new Date()) => {
  const parsed = parseDateValue(value);
  if (!parsed) {
    return '';
  }
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
};

/**
 * Resolve booking window parameter based on selected date
 *
 * @param {Date|string|null} dateValue - Selected date
 * @returns {string} - Window parameter: 'today', 'tomorrow', 'previous'
 *
 * Logic:
 * - diff = 0 (today) → 'today'
 * - diff = 1 (tomorrow) → 'tomorrow'
 * - diff < 0 (past) → 'previous'
 * - diff > 1 (future) → 'today' (F08 fix: was 'tomorrow')
 */
export const resolveBookingsWindow = (dateValue) => {
  const startToday = parseDateValue(new Date());
  const selected = parseDateValue(dateValue) || new Date();
  const todayMidnight = new Date(
    startToday.getFullYear(),
    startToday.getMonth(),
    startToday.getDate()
  );
  const selectedMidnight = new Date(
    selected.getFullYear(),
    selected.getMonth(),
    selected.getDate()
  );
  const diff = Math.round(
    (selectedMidnight.getTime() - todayMidnight.getTime()) / (24 * 60 * 60 * 1000)
  );

  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff < 0) return 'previous';
  return 'today'; // F08 Fix: future dates use 'today' as neutral default
};

export const resolveBookingsRequest = (dateValue) => ({
  windowKey: resolveBookingsWindow(dateValue),
  dateString: formatBookingsDate(dateValue)
});
